import {THREE} from "aframe";
import {buildFloorPlanData} from "./floorPlanDataCalculator";

export interface PanoramaTour {
    header: string;
    defaultStartView?: string;
    panoramas: Panorama[];
    floorPlans: Floorplan[];
    rooms: RoomImage[];
    usesPanoramasAsRoomImages: boolean;
}

export interface Panorama {
    image: string;
    name: string;
    defaultLookAtDirection: THREE.Vector3;
    hotspots: LocationChangeSpot[];
}

type HotSpotIcon = "Spot"|"Arrow"|"Door";
type HotSpotArrowDirection = "Down"|"Up"|"Upright"|"Upleft"|"Downright"|"Downleft";

export interface LocationChangeSpot {
    targetPanorama: string;
    position: THREE.Vector3;
    type: HotSpotIcon;
    arrowDirection?: HotSpotArrowDirection;
}

export interface Floorplan {
    image: string;
    name: string;
    imageHeader: string;
    imageWidth: number;
    imageHeight: number;
    imageTilt: number;
    defaultViewpoint: string;
    hotspots: FloorplanHotspot[];
}

export interface FloorplanHotspot {
    x: number;
    y: number;
    targetPanorama: string;
    targetCameraRotation: number;
}

export interface RoomImage {
    image: string;
    assetId: string;
    imageHeader: string;
    targetPanorama: string;
    targetCameraRotation: number;
}

interface UnrealData {
    ViewpointGroup: UnrealViewPoints;
    Hotspots: UnrealHotSpot[];
    FloorPlans: UnrealFloorPlan[];
}

interface UnrealViewPoints {
    Name: string;
    Viewpoints: UnrealViewPoint[];
}
export interface UnrealViewPoint {
    Name: string;
    DisplayName: string;
    Location: UnrealVector;
    LookAtDirection: UnrealVector;
    Floor: string;
}

export interface UnrealHotSpot {
    Source: string;
    Target: string;
    Offset: UnrealVector;
    Type: string;
    Direction: HotSpotArrowDirection | "Unspecified";
    Icon: HotSpotIcon | "";
}

export interface UnrealFloorPlan {
    Name: string;
    ViewCenter: Omit<UnrealVector, "Z">;
    ViewWidth: number;
    ViewTilt: number;
    DefaultViewpoint: string;
}

export interface UnrealVector {
    X:number;
    Y:number;
    Z:number;
}

interface TourMetadata {
    Name: string;
    ViewPointGroup: string;
    DefaultStartView?: string;
    ImageExtension: string;
    PanoramaImageExtension?: string;
    FloorPlanImageExtension?: string;
    RoomImageExtension?: string;
    FloorPlanImageFolder?: string;
    RoomImageFolder?: string;
}

const showNetworkError = (message: string, response: Response) => console.log(`${response.status} ${response.statusText}: ${message}`);
const showApplicationError = (message: string, error: Error) => console.log(`${message}. ${error.message}`);

export class ApiClient {
    constructor(private readonly contentUrl: string) {
    }

    async getTourMetadata(): Promise<TourMetadata | undefined> {
        const metadataUrl = this.contentUrl + "/assets/tour.json";
        const response = await fetch(metadataUrl);

        if (response.ok) {
            return await response.json() as TourMetadata;
        }
        else {
            showNetworkError("Failed to fetch tour metadata. Url: " + metadataUrl, response);
            return undefined;
        }
    }

    async getPanoramaTour(): Promise<PanoramaTour | undefined> {

        let returnTour: PanoramaTour | undefined = undefined;

        const tourMetadata = await this.getTourMetadata();

        if (!tourMetadata) {
            alert("Tour configuration not found");
            return undefined;
        }

        const tourDataUrl = `${this.contentUrl}/assets/${tourMetadata.ViewPointGroup}.json`;

        //Fetch tour data
        await fetch(tourDataUrl).then(async (response) =>
            {
                if (!response.ok) {
                    showNetworkError("Failed to fetch tour data. Url: " + tourDataUrl, response);
                    alert("VR Tour data not found")
                    return;
                }

                const responseJson = await response.json().catch((error) => {
                    showApplicationError("VR Tour data is not valid json", error);
                    alert("VR Tour data is badly formed")}
                );
                const isUnreal = (val: UnrealData): val is UnrealData => !!val?.ViewpointGroup;

                if (isUnreal(responseJson)) {

                    let floorPlans: Floorplan[] = [];
                    let panoramas: Panorama[] = [];
                    let rooms: RoomImage[] = [];

                    try {
                        // FLOORPLANS
                        floorPlans = await Promise.all(responseJson.FloorPlans.map(async (fp) =>
                            await buildFloorPlanData(fp, responseJson.ViewpointGroup.Viewpoints, this.contentUrl,
                                tourMetadata.FloorPlanImageFolder, tourMetadata.FloorPlanImageExtension ?? tourMetadata.ImageExtension)))
                    }
                    catch (error) {
                        throw new Error("Floor plan data error: " + (error as Error).message);
                    }

                    try {
                        // PANORAMAS
                        panoramas = responseJson.ViewpointGroup.Viewpoints
                            .filter((vp) => vp.Floor) // If view point doesn't have a floor, it's not vr tour view point and should be ignored.
                            .map((vp) => {

                                // PANORAMA HOTSPOTS
                                const panoramaHotSpots = responseJson.Hotspots.filter((hs) => hs.Source === vp.Name);

                                return {
                                    image: `${this.contentUrl}/${vp.Name}.${tourMetadata.PanoramaImageExtension ?? tourMetadata.ImageExtension}`,
                                    name: vp.Name.replace(" ", "_"),
                                    defaultLookAtDirection: new THREE.Vector3(-vp.LookAtDirection.X, vp.LookAtDirection.Z, -vp.LookAtDirection.Y),
                                    hotspots: panoramaHotSpots.map((hs) => {
                                        return {
                                            type: hs.Icon === "" ? "Spot" : hs.Icon,
                                            arrowDirection: hs.Direction !== "Unspecified" ? hs.Direction : (hs.Icon === "Arrow" ? "Down" : undefined),
                                            // Position of hotspot. In Unreal, Z is vertical axel, set it to our vertical (Y).
                                            position: new THREE.Vector3(-hs.Offset.X / 100 * 1.3, hs.Offset.Z / 100 * 1.3, -hs.Offset.Y / 100 * 1.3),
                                            targetPanorama: hs.Target.replace(" ", "_")
                                        }
                                    })
                                }
                            });
                    }
                    catch (error) {
                        throw new Error("Panorama data error: " + (error as Error).message);
                    }

                    try {
                        // ROOMS
                        rooms = responseJson.ViewpointGroup.Viewpoints.filter((vp) => vp.DisplayName && vp.Floor) // If room has a name and floor, it's a room.
                            .map((vp) => {
                                return {
                                    assetId: (tourMetadata.RoomImageFolder ? "r-" : "") + vp.Name.replace(" ", "_"),
                                    // If rooms have separate folder setup, use room images, else use panorama images.
                                    image: `${this.contentUrl}/${tourMetadata.RoomImageFolder ? tourMetadata.RoomImageFolder + "/" : ""}${vp.Name}.${tourMetadata.RoomImageExtension ?? tourMetadata.ImageExtension}`,
                                    targetPanorama: vp.Name.replace(" ", "_"),
                                    imageHeader: vp.DisplayName,
                                    targetCameraRotation: vp.LookAtDirection.X
                                }
                            });
                    }
                    catch (error) {
                        throw new Error("Room data error: " + (error as Error).message);
                    }

                    returnTour =  {
                        header: responseJson.ViewpointGroup.Name,
                        usesPanoramasAsRoomImages: !tourMetadata.RoomImageFolder, // If rooms doesn't have separate folder, use panorama images instead.
                        defaultStartView: tourMetadata.DefaultStartView,
                        panoramas: panoramas,
                        rooms: rooms,
                        floorPlans: floorPlans
                    };
                }
                else {
                    // THIS IS JUST FOR THE DEMO WELLS MODEL (will be deleted later):
                    // Form the image URLs.
                    responseJson.panoramas.forEach((panorama: Panorama) => panorama.image = this.contentUrl + "/" + panorama.image);

                    responseJson.panoramas.forEach((panorama: Panorama) => panorama.hotspots.forEach((hs) =>
                        // @ts-ignore
                        hs.position = hs.type === "spot" ? new THREE.Vector3(hs.position[0] / 8, hs.position[1] / 8, hs.position[2] / 8) :
                        // @ts-ignore
                        new THREE.Vector3(hs.position[0], hs.position[1], hs.position[2])
                    ));
                    responseJson.floorPlans.forEach((floor: Floorplan) => floor.image = this.contentUrl + "/floorplans/" + floor.image);
                    responseJson.rooms.forEach((room: RoomImage) => room.image = `${this.contentUrl}/rooms/${room.image}`);

                    responseJson.usesPanoramasAsRoomImages = false;

                    returnTour = responseJson;
                }
            }
        ).catch((error) => {
            showApplicationError("Error occurred when handling VR Tour data", error);
            alert("VR Tour data is invalid") });

        return returnTour;
    }



}